import * as Sentry from "@sentry/react";

/**
 * Initializes Sentry error tracking for the application.
 *
 * This function should be called once during application startup, typically in
 * the root layout or app initialization file. It only runs in browser environments
 * and requires NEXT_PUBLIC_SENTRY_DSN to be configured.
 *
 * Side effects:
 * - Initializes the Sentry SDK globally
 * - Begins capturing errors and performance data
 * - Logs a warning if DSN is not configured
 */
export function initializeSentry(): void {
  // Only initialize in browser environments
  if (typeof window === "undefined") {
    return;
  }

  const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;
  const environment = process.env.NEXT_PUBLIC_APP_ENV || "development";

  // Skip initialization if DSN is not set
  if (!dsn) {
    console.warn("Sentry DSN not configured, error tracking disabled");
    return;
  }

  try {
    Sentry.init({
      dsn,
      environment,
      // Environment-specific sampling: capture all traces (100%) in dev/staging for debugging,
      // but only 10% in production to reduce costs and performance impact
      tracesSampleRate: environment === "production" ? 0.1 : 1.0,
      // Capture breadcrumbs for better context
      maxBreadcrumbs: 50,
    });
  } catch (error) {
    console.error("Failed to initialize Sentry:", error);
  }
}
